// src/slices/sinhBoSoQr/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  KyPhatHanh,
  QrGenerationJob,
  SinhBoSoQrFilters,
  SinhBoSoQrPagination,
  ModalState,
  toJobStatus,
} from 'pages/SinhBoSoQr/types';
import {
  fetchKyPhatHanhList,
  sinhBoSoQr,
  fetchJobProgress,
  exportQrCsv,
} from './thunk';

interface SinhBoSoQrState {
  kyPhatHanhList: KyPhatHanh[];
  selectedKy: KyPhatHanh | null;
  // Mang job dang chay dong thoi — moi ky phat hanh sinh QR rieng se co 1 phan tu, khong con
  // gioi han chi theo doi duoc 1 job nhu truoc (single activeJob se bi ghi de khi sinh ky thu 2).
  activeJobs: QrGenerationJob[];
  pagination: SinhBoSoQrPagination;
  filters: SinhBoSoQrFilters;
  loading: boolean;
  jobLoading: boolean;
  error: string | null;
  modalState: ModalState;
}

const initialState: SinhBoSoQrState = {
  kyPhatHanhList: [],
  selectedKy: null,
  activeJobs: [],
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
  filters: {
    searchTerm: '',
    trangThaiQR: 'All',
  },
  loading: false,
  jobLoading: false,
  error: null,
  modalState: {
    confirm: false,
  },
};

const sinhBoSoQrSlice = createSlice({
  name: 'SinhBoSoQr',
  initialState,
  reducers: {
    setKyPhatHanhList: (state, action: PayloadAction<KyPhatHanh[]>) => {
      state.kyPhatHanhList = action.payload;
    },
    setSelectedKy: (state, action: PayloadAction<KyPhatHanh | null>) => {
      state.selectedKy = action.payload;
    },
    upsertActiveJob: (state, action: PayloadAction<QrGenerationJob>) => {
      const index = state.activeJobs.findIndex((job) => job.jobId === action.payload.jobId);
      if (index !== -1) {
        state.activeJobs[index] = action.payload;
      } else {
        state.activeJobs.push(action.payload);
      }
    },
    // Khi 1 job bi go khoi danh sach dang active (huy/that bai/dung dot ngot roi nguoi dung tu
    // dong popup) MA CHUA tung hoan tat thanh cong, dong bo lai qrStatus cua dung dong ky phat
    // hanh tuong ung tu "DangChoSinh" (gan optimistic luc bam Sinh) ve lai "Pending" — neu khong,
    // dong do bi ket o trang thai "Đang sinh" mai mai tren man Sinh Bộ Số & Mã QR cho toi khi F5,
    // du job da bi huy va nguoi dung phai sinh lai duoc ngay (khong doi fetch lai ca danh sach).
    removeActiveJob: (state, action: PayloadAction<string>) => {
      const removedJob = state.activeJobs.find((job) => job.jobId === action.payload);
      state.activeJobs = state.activeJobs.filter((job) => job.jobId !== action.payload);

      if (removedJob && removedJob.status !== 'completed') {
        const rowIndex = state.kyPhatHanhList.findIndex((ky) => ky.id === removedJob.lotteryPeriodId);
        if (rowIndex !== -1 && state.kyPhatHanhList[rowIndex].qrStatus === 'DangChoSinh') {
          state.kyPhatHanhList[rowIndex].qrStatus = 'Pending';
        }
      }
    },
    updateJobProgress: (state, action: PayloadAction<{ jobId: string } & Partial<QrGenerationJob>>) => {
      const index = state.activeJobs.findIndex((job) => job.jobId === action.payload.jobId);
      if (index !== -1) {
        state.activeJobs[index] = { ...state.activeJobs[index], ...action.payload };
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setJobLoading: (state, action: PayloadAction<boolean>) => {
      state.jobLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setPagination: (state, action: PayloadAction<Partial<SinhBoSoQrPagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setFilters: (state, action: PayloadAction<Partial<SinhBoSoQrFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        searchTerm: '',
        trangThaiQR: 'All',
      };
      state.pagination.currentPage = 1;
    },
    openModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modalState[action.payload] = false;
    },
    closeAllModals: (state) => {
      state.modalState = {
        confirm: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch kỳ phát hành list
      .addCase(fetchKyPhatHanhList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKyPhatHanhList.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.kyPhatHanhList = payload.items || payload.data || payload || [];
        state.pagination.totalItems = payload.totalItems ?? payload.total ?? state.kyPhatHanhList.length;
        state.pagination.totalPages = payload.totalPages ?? Math.ceil(state.pagination.totalItems / state.pagination.pageSize);
      })
      .addCase(fetchKyPhatHanhList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Sinh bộ số QR
      .addCase(sinhBoSoQr.pending, (state) => {
        state.jobLoading = true;
        state.error = null;
      })
      .addCase(sinhBoSoQr.fulfilled, (state, action) => {
        state.jobLoading = false;
        const jobData = action.payload as Partial<QrGenerationJob>;
        const lotteryPeriodId = jobData.lotteryPeriodId ?? state.selectedKy?.id ?? action.meta.arg;
        const job: QrGenerationJob = {
          jobId: jobData.jobId || '',
          lotteryPeriodId,
          status: toJobStatus(jobData.status),
          totalTickets: jobData.totalTickets ?? state.selectedKy?.ticketQuantity ?? 0,
          processedTickets: jobData.processedTickets ?? 0,
          progressPercent: jobData.progressPercent ?? 0,
          errorMessage: jobData.errorMessage ?? null,
          startedAt: jobData.startedAt ?? null,
          completedAt: jobData.completedAt ?? null,
          createdAt: jobData.createdAt ?? new Date().toISOString(),
        };
        const index = state.activeJobs.findIndex((j) => j.jobId === job.jobId);
        if (index !== -1) {
          state.activeJobs[index] = job;
        } else {
          state.activeJobs.push(job);
        }

        // Cap nhat ngay trang thai dong trong danh sach thanh "Dang sinh" — khong doi fetch
        // lai list moi disable duoc nut, tranh nguoi dung bam sinh lai trong luc cho response.
        const rowIndex = state.kyPhatHanhList.findIndex((ky) => ky.id === lotteryPeriodId);
        if (rowIndex !== -1) {
          state.kyPhatHanhList[rowIndex].qrStatus = 'DangChoSinh';
        }
      })
      .addCase(sinhBoSoQr.rejected, (state, action) => {
        state.jobLoading = false;
        state.error = action.payload as string;
      })

      // Fetch job progress
      .addCase(fetchJobProgress.fulfilled, (state, action) => {
        const payload = action.payload as (Partial<QrGenerationJob> & { jobId?: string }) | undefined;
        const jobId = payload?.jobId || (action.meta.arg as string);
        const index = state.activeJobs.findIndex((job) => job.jobId === jobId);
        if (index !== -1 && payload) {
          state.activeJobs[index] = {
            ...state.activeJobs[index],
            ...payload,
            status: toJobStatus(payload.status),
          };
        }
      })
      .addCase(fetchJobProgress.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Export CSV
      .addCase(exportQrCsv.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportQrCsv.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportQrCsv.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setKyPhatHanhList,
  setSelectedKy,
  upsertActiveJob,
  removeActiveJob,
  updateJobProgress,
  setLoading,
  setJobLoading,
  setError,
  clearError,
  setPage,
  setPageSize,
  setPagination,
  setFilters,
  clearFilters,
  openModal,
  closeModal,
  closeAllModals,
} = sinhBoSoQrSlice.actions;

export default sinhBoSoQrSlice.reducer;
